/**
 * React Query hooks for Serial CRUD operations.
 * Uses tRPC client for type-safe API calls.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpc } from "../lib/trpc";
import type { Serial } from "../types";

// List serials, optionally filtered by groupId
export function useSerials(groupId?: string) {
  return useQuery({
    queryKey: ["serials", "list", groupId ? { groupId } : {}],
    queryFn: () =>
      trpc.serial.list.query(groupId ? { groupId } : {}),
  }) as { data: Serial[]; isLoading: boolean; isError: boolean; error: any };
}

// Get a single serial by ID
export function useSerial(serialId: string) {
  return useQuery({
    queryKey: ["serials", "get", serialId],
    queryFn: () => trpc.serial.get.query(serialId),
    enabled: !!serialId,
  }) as { data: Serial | undefined; isLoading: boolean; isError: boolean; error: any };
}

// Create a new serial
export function useCreateSerial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { key: string; description?: string; groupId?: string }) => {
      return await trpc.serial.create.mutate(input);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serials"] });
    },
  });
}

// Update an existing serial
export function useUpdateSerial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string } & Partial<{ key: string; description: string; groupId: string; paletteId: string | null }>) => {
      return await trpc.serial.update.mutate(input as any);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serials"] });
    },
  });
}

// Delete a serial
export function useDeleteSerial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      return await trpc.serial.delete.mutate(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["serials"] });
    },
  });
}